<template>
  <Dialog
    :visible="modelValue"
    modal
    header="Add Note"
    class="w-full max-w-md"
    :draggable="false"
    @update:visible="emit('update:modelValue', $event)"
  >
    <div class="space-y-3 pt-2">
      <Textarea
        v-model="content"
        rows="4"
        autoResize
        class="w-full"
        placeholder="Note content..."
        data-testid="note-content-input"
        :class="{ 'p-invalid': getErrorMessage('content') }"
      />
      <small v-if="getErrorMessage('content')" class="text-red-500">{{ getErrorMessage('content') }}</small>

      <div class="flex items-center gap-2">
        <Checkbox v-model="visibleToApplicant" binary inputId="note-visible" data-testid="note-visible-checkbox" />
        <label for="note-visible" class="text-sm text-gray-700">Visible to applicant</label>
      </div>
    </div>

    <template #footer>
      <Button size="small" label="Cancel" severity="secondary" text data-testid="note-cancel" @click="close" />
      <Button size="small"
        label="Add Note"
        :loading="addingNote"
        :disabled="addingNote || content.trim().length === 0"
        data-testid="note-submit"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useFormErrors } from '@/composables/useFormErrors';
import { useTranscriptMutations } from '@/composables/transcript/useTranscriptMutations';

const props = defineProps<{
  modelValue: boolean;
  requestId: string | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  added: [];
}>();

const content = ref('');
const visibleToApplicant = ref(false);
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();
const { addRequestNote, addingNote } = useTranscriptMutations();

watch(() => props.modelValue, (open) => {
  if (open) {
    content.value = '';
    visibleToApplicant.value = false;
    clearErrors();
  }
});

const close = () => emit('update:modelValue', false);

const submit = async() => {
  if (!props.requestId) return;
  clearErrors();
  try {
    await addRequestNote(props.requestId, content.value, visibleToApplicant.value);
    emit('added');
    close();
  } catch (err) {
    handleServerErrors(err);
  }
};
</script>
